import { ChevronDown, ChevronRight } from 'lucide-react'
import { useContext, useState } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import AnimateHeightAndOpacity from 'components/reusable/AnimateHeightAndOpacity'
import { Banner, Button } from 'slab'
import { useGetBrokersMembersMemberIdEnrollments } from 'utils/web_api'
import { type BrokersEnrollment as BrokersEnrollmentType } from 'utils/web_api.schemas'

import BrokersEnrollment, {
  BrokersEnrollmentSkeleton,
} from './BrokersEnrollment'

const coverageTypeOrder: Record<
  BrokersEnrollmentType['coverage_type'],
  number
> = {
  medical: 0,
  dental: 1,
  vision: 2,
}

function sortEnrollmentsByEnrollmentType (
  enrollments: BrokersEnrollmentType[]
): BrokersEnrollmentType[] {
  return (
    enrollments?.sort(
      (a: BrokersEnrollmentType, b: BrokersEnrollmentType) => {
        return (
          coverageTypeOrder[a.coverage_type] -
          coverageTypeOrder[b.coverage_type]
        )
      }
    ) || []
  )
}
interface BrokersMembersEnrollmentsProps {
  memberId: string
}

const BrokersMembersEnrollments = ({
  memberId,
}: BrokersMembersEnrollmentsProps) => {
  const { planYear } = useContext(BrokersMemberContext)
  const [showCanceledEnrollments, setShowCanceledEnrollments] = useState(false)
  const {
    data: allEnrollments,
    isFetching,
    error,
  } = useGetBrokersMembersMemberIdEnrollments(
    memberId,
    {
      plan_year: planYear,
      include_canceled: true,
    },
    {
      query: {
        // Enable queries when we have valid data
        enabled: !!memberId && !!planYear,
        refetchOnWindowFocus: false,
      },
    }
  )

  const handleViewCanceledEnrollments = () => {
    setShowCanceledEnrollments(!showCanceledEnrollments)
  }

  if (isFetching) {
    return (
      <div>
        <BrokersEnrollmentSkeleton />
        <BrokersEnrollmentSkeleton />
        <BrokersEnrollmentSkeleton />
      </div>
    )
  }

  if (error) {
    return <Banner type='critical' title='Error loading enrollments' />
  }
  const enrollments = allEnrollments?.data?.filter((enrollment) => enrollment.state !== 'canceled')
  const canceledEnrollments = allEnrollments?.data?.filter((enrollment) => enrollment.state === 'canceled')

  if (!enrollments || enrollments.length === 0) {
    return (
      <Banner type='caution' title='No confirmed enrollments found'>
        This member currently has no confirmed enrollments for the specified
        plan year.
      </Banner>
    )
  }

  const sortedEnrollments = sortEnrollmentsByEnrollmentType(enrollments)

  // NOTE: We're currently fetching the list of medical enrollments more or less just for the IDs of the
  // enrollments we should be displaying. Each component then fetches its own set of denormalized enrollments
  // data for the specific enrollment details. This allows us to specify which enrollments and their associated
  // cache values should be updated directly instead of refreshing the entire list.
  return (
    <div>
      {sortedEnrollments.map((enrollment) => (
        <BrokersEnrollment
          key={enrollment.id}
          memberId={memberId}
          enrollmentId={enrollment.id}
          editCoveragesPath={`/brokers/members/${memberId}/enrollments/${enrollment.id}/edit_coverages`}
        />
      ))}
      {canceledEnrollments && canceledEnrollments.length > 0 && (
        <>
          <Button
            onClick={handleViewCanceledEnrollments}
            variant='tertiary'
            rightIcon={showCanceledEnrollments ? ChevronDown : ChevronRight}
          >
            {showCanceledEnrollments ? 'Hide canceled enrollments' : 'View canceled enrollments'}
          </Button>
          <AnimateHeightAndOpacity isOpen={showCanceledEnrollments}>
            {canceledEnrollments.map((enrollment) => (
              <BrokersEnrollment
                key={enrollment.id}
                memberId={memberId}
                enrollmentId={enrollment.id}
                editCoveragesPath={`/brokers/members/${memberId}/enrollments/${enrollment.id}/edit_coverages`}
              />
            ))}
          </AnimateHeightAndOpacity>
        </>
      )}
    </div>
  )
}

export default BrokersMembersEnrollments
